import React, { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { Users, Calendar, ShoppingBag, DollarSign, Heart, Mail, Gem, Camera, Scale, Briefcase, BarChart3, Wallet, Store, UserPlus, ExternalLink, Sparkles, Shield, RotateCcw, Microscope, Bot, FlaskConical, Server, FileText } from "lucide-react";
import { toast } from "sonner";
import Explainer from "../components/Explainer";

// ---------- Stat card ----------
function StatCard({ icon: Icon, value, label, explainerId }) {
  return (
    <div className="card p-5">
      <Icon size={18} strokeWidth={1.5} className="text-[#C9A961]" />
      <p className="font-serif text-3xl text-[#1A2424] mt-3">{value}</p>
      <p className="label mt-1">
        {label} {explainerId && <Explainer id={explainerId} size={10} />}
      </p>
    </div>
  );
}

// ---------- Stats grid ----------
function StatsGrid({ stats }) {
  const items = useMemo(
    () => [
      { label: "Users", value: stats.users_count, icon: Users, explainerId: "dash.stats.users" },
      { label: "Workshops", value: stats.workshops_count, icon: Calendar, explainerId: "dash.stats.workshops" },
      { label: "Registrations", value: stats.registrations_count, icon: Heart, explainerId: "dash.stats.regs" },
      { label: "Orders", value: stats.orders_count, icon: ShoppingBag, explainerId: "dash.stats.orders" },
      { label: "Products", value: stats.products_count, icon: ShoppingBag, explainerId: "dash.stats.products" },
      { label: "Newsletter", value: stats.newsletter_count, icon: Mail, explainerId: "dash.stats.newsletter" },
      { label: "Sponsors", value: stats.sponsors_count, icon: Gem, explainerId: "dash.stats.sponsors" },
      { label: "Revenue", value: `$${(stats.total_revenue || 0).toFixed(0)}`, icon: DollarSign, explainerId: "dash.stats.revenue" },
    ],
    [stats]
  );
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="admin-stats">
      {items.map((s) => (
        <StatCard key={s.label} icon={s.icon} value={s.value} label={s.label} explainerId={s.explainerId} />
      ))}
    </div>
  );
}

// ---------- POD margin tile (Printful + Lulu) ----------
function PodMarginTile() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(0);   // 0 = all-time
  const [err, setErr] = useState(false);

  useEffect(() => {
    api
      .get(`/admin/foundation/revenue/pod-margin?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, [days]);

  if (err) return null;

  const fmt = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const rows = data
    ? [
        { id: "printful", label: "Printful (merch)", bucket: data.printful },
        { id: "lulu", label: "Lulu (books)", bucket: data.lulu },
        { id: "combined", label: "Combined POD", bucket: data.combined, bold: true },
      ]
    : [];

  return (
    <section className="card p-6 mt-8" data-testid="admin-pod-margin">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <p className="label text-[#C9A961]">Foundation revenue · POD margin</p>
          <h2 className="font-serif text-xl mt-1">Printful + Lulu</h2>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {[
            { v: 0, l: "All-time" },
            { v: 30, l: "30 days" },
            { v: 90, l: "90 days" },
            { v: 365, l: "1 year" },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setDays(opt.v)}
              data-testid={`pod-margin-window-${opt.v}`}
              className={`px-3 py-1 rounded-full transition border ${
                days === opt.v
                  ? "border-[#476B6B] text-[#476B6B] bg-[#F0F6F4]"
                  : "border-[#E5E1D8] text-[#5C6B6B] hover:text-[#1A2424]"
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <p className="text-sm text-[#5C6B6B] mt-4">Loading POD margin…</p>
      ) : (
        <>
          <div className="overflow-x-auto mt-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E1D8]">
                  <th className="text-left py-2 label">Provider</th>
                  <th className="text-right py-2 label">Revenue</th>
                  <th className="text-right py-2 label">POD cost</th>
                  <th className="text-right py-2 label">Margin</th>
                  <th className="text-right py-2 label">Margin %</th>
                  <th className="text-right py-2 label">Units</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`pod-margin-row-${r.id}`}
                    className={`border-b border-[#E5E1D8] last:border-0 ${
                      r.bold ? "font-medium bg-[#FAF8F5]" : ""
                    }`}
                  >
                    <td className="py-2.5">{r.label}</td>
                    <td className="py-2.5 text-right">{fmt(r.bucket.revenue)}</td>
                    <td className="py-2.5 text-right text-[#5C6B6B]">{fmt(r.bucket.cost)}</td>
                    <td className="py-2.5 text-right text-[#2E5C46]">{fmt(r.bucket.margin)}</td>
                    <td className="py-2.5 text-right">
                      {r.bucket.margin_pct ? `${r.bucket.margin_pct}%` : "—"}
                    </td>
                    <td className="py-2.5 text-right text-[#5C6B6B]">{r.bucket.units}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.missing_cost_skus?.length > 0 && (
            <div
              className="mt-4 text-xs text-[#C9A961] bg-[#FFF8E8] border border-[#F0E2B2] rounded px-3 py-2"
              data-testid="pod-margin-missing-cost"
            >
              <strong>Data hygiene:</strong> {data.missing_cost_skus.length} POD SKU
              {data.missing_cost_skus.length === 1 ? "" : "s"} {data.missing_cost_skus.length === 1 ? "is" : "are"} missing a
              base cost and {data.missing_cost_skus.length === 1 ? "was" : "were"} treated as 100% margin:{" "}
              <span className="text-[#5C6B6B]">{data.missing_cost_skus.slice(0, 5).join(", ")}{data.missing_cost_skus.length > 5 ? "…" : ""}</span>
            </div>
          )}

          <p className="text-xs text-[#5C6B6B] mt-3">
            {data.order_count} paid order{data.order_count === 1 ? "" : "s"} in window.
            Margin = retail revenue − POD wholesale cost. Foundation revenue
            (birthright-fulfilled merch, workshops, subscriptions) is tracked
            separately under{" "}
            <Link to="/admin/reports" className="underline">Foundation reports</Link>.
          </p>
        </>
      )}
    </section>
  );
}

// ---------- Quick action card ----------
function QuickActionCard({ to, icon: Icon, title, description }) {
  return (
    <Link to={to} className="card card-hover p-7">
      <Icon size={22} strokeWidth={1.5} className="text-[#C9A961]" />
      <h3 className="font-serif text-xl mt-3">{title}</h3>
      <p className="text-sm text-[#5C6B6B] mt-2">{description}</p>
    </Link>
  );
}

// ---------- Overview tab ----------
function OverviewTab() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="admin-overview">
      <QuickActionCard to="/admin/workshops" icon={Calendar} title="Manage Workshops" description="Create, edit, cancel, duplicate" />
      <QuickActionCard to="/admin/products" icon={ShoppingBag} title="Manage Products" description="Merch and workshop materials" />
      <QuickActionCard to="/admin/photos" icon={Camera} title="Photo Moderation" description="Approve participant uploads" />
      <QuickActionCard to="/admin/governance" icon={Scale} title="Governance & Legal" description="Defaults, members, indemnification, audit" />
      <QuickActionCard to="/admin/partners" icon={Briefcase} title="Partner applications" description="Approve, reject, invite partners" />
      <QuickActionCard to="/admin/partners/prospects" icon={Briefcase} title="Partner prospects (all types)" description="Outreach tracker · Explore-before-Embrace invitations" />
      <QuickActionCard to="/admin/gallery/prospects" icon={Briefcase} title="Featured Artist prospects" description="Track outreach · issue Foundation invitations" />
      <QuickActionCard to="/admin/foundation-roles" icon={UserPlus} title="Foundation roles" description="Open board seats & job descriptions" />
      <QuickActionCard to="/admin/foundation-applications" icon={UserPlus} title="Role applications" description="Triage applicants for open board seats" />
      <QuickActionCard to="/admin/vendor-products" icon={Store} title="Vendor catalog" description="Moderate vendor-submitted products" />
      <QuickActionCard to="/admin/studio/queue" icon={FlaskConical} title="Studio queue" description="Approve vendor AI Studio submissions" />
      <QuickActionCard to="/admin/lulu-ops" icon={Server} title="Lulu cutover" description="Validate POD presets · manage webhooks" />
      <QuickActionCard to="/admin/email-ops" icon={Mail} title="Email cutover" description="Resend status · flip dry-run → live" />
      <QuickActionCard to="/admin/payouts" icon={Wallet} title="Partner payouts" description="Referral ledger & disbursements" />
      <QuickActionCard to="/admin/subscriptions" icon={Wallet} title="Subscriptions" description="Active, cancelled, revoke + refund" />
      <QuickActionCard to="/admin/ombudsman" icon={Shield} title="Ombudsman queue" description="Disputes + flagged DM threads" />
      <QuickActionCard to="/admin/refunds" icon={RotateCcw} title="Refunds & Clawbacks" description="Fire refund cascades, resolve clawbacks" />
      <QuickActionCard to="/admin/research" icon={Microscope} title="Research moderation" description="Approve, request changes, reject submissions" />
      <QuickActionCard to="/admin/ai-usage" icon={Bot} title="AI usage" description="Per-partner AI spend + wallet balances" />
      <QuickActionCard to="/admin/system" icon={Server} title="System" description="Support emails · data migrations · operator console" />
      <QuickActionCard to="/admin/legal/agreements" icon={FileText} title="Partnership agreements" description="Publish, audit, and gate write-features" />
      <QuickActionCard to="/admin/legal/ratifications" icon={FileText} title="Legal ratifications" description="Mark docs counsel-ratified + track redlines" />
      <QuickActionCard to="/admin/settings/counsel" icon={FileText} title="Counsel credentials" description="Rotate the counsel email + password" />
      <QuickActionCard to="/admin/partner-sales-reports" icon={ExternalLink} title="Off-site sales reports" description="Reconcile partner-reported revenue" />
      <QuickActionCard to="/admin/featured" icon={Sparkles} title="Featured & Founding" description="Grant/revoke featured slots, founding-partner cap" />
      <QuickActionCard to="/admin/reports" icon={BarChart3} title="Foundation reports" description="Engagement, revenue, payouts" />
      <QuickActionCard to="/admin/content" icon={Heart} title="Foundation Content" description="Mission, about, governance" />
    </div>
  );
}

// ---------- Users tab ----------
function UsersTab({ users, onChangeRole }) {
  return (
    <div className="card overflow-hidden" data-testid="admin-users-table">
      <table className="w-full text-sm">
        <thead className="bg-[#FAF8F5] border-b border-[#E5E1D8]">
          <tr>
            <th className="text-left p-4 label">Name</th>
            <th className="text-left p-4 label">Email</th>
            <th className="text-left p-4 label">Role</th>
            <th className="text-left p-4 label">Joined</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-[#E5E1D8] last:border-0">
              <td className="p-4">
                {u.first_name} {u.last_name}
              </td>
              <td className="p-4 text-[#5C6B6B]">{u.email}</td>
              <td className="p-4">
                <select
                  value={u.role}
                  onChange={(e) => onChangeRole(u.id, e.target.value)}
                  className="input-field !py-1 text-xs"
                  data-testid={`user-role-${u.id}`}
                >
                  <option value="participant">Participant</option>
                  <option value="facilitator">Facilitator</option>
                  <option value="admin">Admin</option>
                </select>
              </td>
              <td className="p-4 text-[#5C6B6B] text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Messages tab ----------
function MessagesTab({ messages }) {
  if (messages.length === 0) {
    return <p className="text-sm text-[#5C6B6B]" data-testid="admin-messages">No messages.</p>;
  }
  return (
    <div className="space-y-3" data-testid="admin-messages">
      {messages.map((m) => (
        <div key={m.id} className="card p-5">
          <div className="flex items-center justify-between">
            <p className="font-medium">
              {m.first_name} {m.last_name || ""}
            </p>
            <p className="text-xs text-[#5C6B6B]">{new Date(m.created_at).toLocaleDateString()}</p>
          </div>
          <p className="text-xs text-[#5C6B6B] mt-1">
            {m.email} · {m.phone}
          </p>
          <p className="text-xs text-[#C9A961] uppercase tracking-wider mt-2">{m.subject}</p>
          <p className="text-sm mt-2 whitespace-pre-line">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

// ---------- Email log tab ----------
function EmailLogTab({ items, realSendEnabled }) {
  return (
    <div data-testid="admin-email-log">
      <div className={`card p-4 mb-4 ${realSendEnabled ? "bg-[#F0F6F4]" : "bg-[#FFF8E8]"}`}>
        <p className="text-sm">
          {realSendEnabled ? (
            <>
              <strong className="text-[#2E5C46]">Live mode.</strong>{" "}
              Emails are sending through Resend to real inboxes.
            </>
          ) : (
            <>
              <strong className="text-[#C9A961]">Dry-run mode.</strong>{" "}
              Emails are queued for inspection only — no real sends. Add a valid <code>RESEND_API_KEY</code>{" "}
              and set <code>EMAIL_DRY_RUN=false</code> in <code>backend/.env</code> to go live.
            </>
          )}
        </p>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-[#5C6B6B]">No emails sent yet.</p>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF8F5] border-b border-[#E5E1D8]">
              <tr>
                <th className="text-left p-3 label">When</th>
                <th className="text-left p-3 label">Template</th>
                <th className="text-left p-3 label">Subject</th>
                <th className="text-left p-3 label">To</th>
                <th className="text-left p-3 label">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-b border-[#E5E1D8] last:border-0" data-testid={`email-row-${e.id}`}>
                  <td className="p-3 text-xs text-[#5C6B6B]">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="p-3 text-xs">{e.template || "—"}</td>
                  <td className="p-3 text-xs">{e.subject}</td>
                  <td className="p-3 text-xs text-[#5C6B6B]">{(e.to || []).join(", ")}</td>
                  <td className="p-3">
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        e.status === "sent"
                          ? "bg-[#2E5C46]/10 text-[#2E5C46]"
                          : e.status === "failed"
                          ? "bg-[#B86A5C]/10 text-[#B86A5C]"
                          : "bg-[#C9A961]/15 text-[#C9A961]"
                      }`}
                    >
                      {e.status?.replace("_", " ") || "queued"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- Tab switcher ----------
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "users", label: "Users" },
  { id: "messages", label: "Contact Messages" },
  { id: "emails", label: "Email log" },
];

function TabBar({ active, onChange }) {
  return (
    <div className="mt-10 border-b border-[#E5E1D8] flex gap-1 overflow-x-auto no-scrollbar">
      {TABS.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition ${
            active === t.id ? "border-[#476B6B] text-[#476B6B]" : "border-transparent text-[#5C6B6B] hover:text-[#1A2424]"
          }`}
          data-testid={`admin-tab-${t.id}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ---------- Main component ----------
export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [emailLog, setEmailLog] = useState({ items: [], real_send_enabled: false });
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    api.get("/dashboard/admin").then((r) => setStats(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/contact/messages").then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "emails") {
      api.get("/admin/email-log?limit=100").then((r) => setEmailLog(r.data)).catch(() => {});
    }
  }, [tab]);

  const changeRole = async (userId, role) => {
    await api.put(`/admin/users/${userId}/role`, { role });
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role } : u)));
    toast.success(`Role updated to ${role}`);
  };

  if (!stats) {
    return (
      <div className="container-page py-20" data-testid="admin-loading">
        Loading...
      </div>
    );
  }

  return (
    <div className="container-page py-12" data-testid="admin-dashboard-page">
      <span className="label">Admin</span>
      <h1 className="editorial-h1 mt-2">Foundation overview</h1>
      <div className="divider-flame" />
      <StatsGrid stats={stats} />
      <PodMarginTile />
      <TabBar active={tab} onChange={setTab} />
      <div className="mt-8">
        {tab === "overview" && <OverviewTab />}
        {tab === "users" && <UsersTab users={users} onChangeRole={changeRole} />}
        {tab === "messages" && <MessagesTab messages={messages} />}
        {tab === "emails" && <EmailLogTab items={emailLog.items} realSendEnabled={emailLog.real_send_enabled} />}
      </div>
    </div>
  );
}
